type LeadFormEventParams = {
  elementName: string;
  isCorporate: boolean;
  /** Sanity careLeadForm versionId (e.g. "v1", "v3-urgency") — sent as form_version_id */
  formVersionId: string;
  pageZone?: string;
  pagePath: string;
  pageUrl: string;
};

/**
 * Shared base properties for all Lead Form Segment events.
 * Intentionally does not inherit from getTrackedClickEventBody — Lead Form events
 * have distinct Segment event names and should not carry event: "tracked_click"
 * as a payload property.
 *
 * page_zone defaults to "sitebuilder" (Lead Form lives on SiteBuilder pages).
 */
function leadFormEventBase({
  elementName,
  isCorporate,
  formVersionId,
  pageZone,
  pagePath,
  pageUrl,
}: LeadFormEventParams) {
  return {
    element_name: elementName,
    form_variant: "lfb" as const,
    form_version_id: formVersionId,
    is_corporate: isCorporate,
    lang: "en",
    page_path: pagePath,
    page_url: pageUrl,
    page_zone: pageZone ?? "sitebuilder",
  };
}

/**
 * Fired when a user clicks Continue on a multi-select step.
 * Segment event name: "Lead Form Multi Select Continued".
 * element_name = the input's fieldName from Sanity (e.g. "careType").
 * selected_options = array of metricsLabel ?? value ?? label for each selected option.
 */
function LeadFormMultiSelectContinueEvent(
  params: LeadFormEventParams & { selectedOptions: string[] },
) {
  return {
    ...leadFormEventBase(params),
    selected_options: params.selectedOptions,
  };
}

/**
 * Fired when a user selects an option in a radio input.
 * Segment event name: "Lead Form Radio Selected".
 * element_name = the input's fieldName from Sanity (e.g. "careFor").
 * selected_value = option.metricsLabel ?? option.value ?? option.label.
 */
function LeadFormRadioSelectionEvent(
  params: LeadFormEventParams & { selectedValue: string },
) {
  return {
    ...leadFormEventBase(params),
    selected_value: params.selectedValue,
  };
}

/**
 * Fired when a user advances a step via a Continue button.
 * Segment event name: "Lead Form Step Continued".
 * element_name = the step's id from Sanity (e.g. "contact").
 * form_variant is always "lfb" — not configurable.
 */
function LeadFormStepContinueEvent(params: LeadFormEventParams) {
  return leadFormEventBase(params);
}

export {
  LeadFormMultiSelectContinueEvent,
  LeadFormRadioSelectionEvent,
  LeadFormStepContinueEvent,
};
